import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChangeReply, Ticket, TicketConsistency } from '../api/types.js';
import { useApiClient } from './use-api-client.js';

// Ticket plans of the server and the operator's decisions on them (ADR-026 step 1), then on the change (step 2)

export function useTickets(serverId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['tickets', serverId],
    queryFn: () => api.tickets.list(serverId),
    select: (d) => d.tickets,
    refetchInterval: 60_000,
    refetchOnWindowFocus: 'always',
  });
}

/** The latest consistency check across the plans: same request as the list */
export function useTicketConsistency(serverId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['tickets', serverId],
    queryFn: () => api.tickets.list(serverId),
    select: (d): TicketConsistency | null => d.consistency ?? null,
    refetchInterval: 60_000,
  });
}

/** Starts one consistency check; the API answers 409 while one runs */
export function useCheckConsistency(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.tickets.checkConsistency(serverId),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['tickets', serverId] }),
  });
}

/** The teammates a ticket can be handed to; fetched only once the operator chooses Reassign */
export function useTicketAssignees(serverId: string, ref: string, enabled: boolean) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['ticket-assignees', serverId, ref],
    queryFn: () => api.tickets.assignees(serverId, ref),
    select: (d) => d.assignees,
    enabled,
    staleTime: 60_000,
  });
}

/** The teammates aidev suggests for the ticket, from the same request as the assignees */
export function useTicketSuggestions(serverId: string, ref: string, enabled: boolean) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['ticket-assignees', serverId, ref],
    queryFn: () => api.tickets.assignees(serverId, ref),
    select: (d) => d.suggestions ?? [],
    enabled,
    staleTime: 60_000,
  });
}

export type TicketDecision =
  | { decision: 'implement'; approach: string; trustAuthors?: string[] }
  | { decision: 'reassign'; login: string; comment: string }
  | { decision: 'reply'; comment: string; updatePlan?: boolean }
  | { decision: 'dismiss' }
  | { decision: 'open-pr'; commit: string; title: string; body: string; draft: boolean; replies?: ChangeReply[] }
  | { decision: 'post-replies'; replies: ChangeReply[] }
  | { decision: 'request-changes'; note: string }
  | { decision: 'next-step'; approach: string }
  | { decision: 'drop' }
  | { decision: 'sub-tickets'; subTickets: Array<{ title: string; goal: string; dependsOn: number[] }> };

export function useDecideTicket(serverId: string, ref: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (d: TicketDecision): Promise<{ ticket: Ticket; note?: string }> => {
      switch (d.decision) {
        case 'sub-tickets': {
          const r = await api.tickets.createSubTickets(serverId, ref, d.subTickets);
          return { ticket: r.ticket, ...(r.complete ? {} : { note: 'Some sub-tickets were not created: see each one below, and try again.' }) };
        }
        case 'implement': {
          const r = await api.tickets.implement(serverId, ref, d.approach, d.trustAuthors);
          return { ticket: r.ticket, ...(r.run.started ? {} : { note: r.run.reason }) };
        }
        case 'reassign':
          return api.tickets.reassign(serverId, ref, d.login, d.comment);
        case 'reply': {
          const r = await api.tickets.reply(serverId, ref, d.comment, d.updatePlan === true);
          if (!r.run) return { ticket: r.ticket };
          return { ticket: r.ticket, note: r.run.started ? 'Reply posted. The session is updating the plan with it: the new plan shows here after its run.' : r.run.reason };
        }
        case 'open-pr':
          return api.tickets.openPullRequest(serverId, ref, { commit: d.commit, title: d.title, body: d.body, draft: d.draft, ...(d.replies?.length ? { replies: d.replies } : {}) });
        case 'post-replies': {
          const r = await api.tickets.postReplies(serverId, ref, d.replies);
          return { ticket: r.ticket, note: `${d.replies.length === 1 ? 'Reply' : 'Replies'} posted on the pull request under your name.` };
        }
        case 'request-changes': {
          const r = await api.tickets.requestChanges(serverId, ref, d.note);
          return { ticket: r.ticket, note: 'Sent to the session: its new version of the change shows here after its run.' };
        }
        case 'next-step': {
          const r = await api.tickets.nextStep(serverId, ref, d.approach);
          return { ticket: r.ticket, note: 'Sent to the session: the change of the next step shows here after its run.' };
        }
        case 'drop':
          return api.tickets.dropChange(serverId, ref);
        default:
          return api.tickets.dismiss(serverId, ref);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tickets', serverId] });
      void qc.invalidateQueries({ queryKey: ['inbox', serverId] });
      void qc.invalidateQueries({ queryKey: ['actors', serverId] });
    },
  });
}

/** The accounts the operator trusts on the server (ADR-029) */
export function useTrustedActors(serverId: string, enabled: boolean) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['actors', serverId],
    queryFn: () => api.actors.list(serverId),
    select: (d) => d.actors,
    enabled,
  });
}

export function useRevokeActor(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) => api.actors.revoke(serverId, ref),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['actors', serverId] });
      void qc.invalidateQueries({ queryKey: ['tickets', serverId] });
    },
  });
}

/** A GitLab issue's plan, prepared on the operator's click (ADR-041 step 3a); it comes back in the list */
export function useStartTicket(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (issue: string) => api.tickets.start(serverId, issue),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tickets', serverId] }); },
  });
}
